import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Admin Auth Store
export const useAdminStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      authType: null, // 'jwt' or 'session'
      
      setAuth: (token, user) => set({ 
        token, 
        user, 
        isAuthenticated: true,
        authType: token === 'session' ? 'session' : 'jwt'
      }),
      
      logout: () => {
        // Call logout API if session-based
        if (get().authType === 'session') {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/auth/logout`, {
            method: 'POST',
            credentials: 'include'
          }).catch(() => {});
        }
        set({ token: null, user: null, isAuthenticated: false, authType: null });
      },
      
      getToken: () => get().token,
      getAuthType: () => get().authType,
    }),
    {
      name: 'admin-auth-storage',
    }
  )
);

// Portal Auth Store
export const usePortalStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      tenant: null,
      isAuthenticated: false,
      authType: null, // 'jwt' or 'session'
      
      setAuth: (...args) => {
        // Supports BOTH legacy positional form `setAuth(token, user, tenant)`
        // and new object form `setAuth({ token, user, tenant, authType })`.
        let token, user, tenant, authType;
        if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
          ({ token, user, tenant, authType } = args[0]);
        } else {
          [token, user, tenant] = args;
        }
        set({
          token,
          user,
          tenant,
          isAuthenticated: true,
          authType: authType || (token === 'session' ? 'session' : 'jwt'),
        });
      },

      // Refresh tenant + user from /auth/me — keeps sidebar/breadcrumb
      // names in sync when an admin renames the tenant.
      setIdentity: (user, tenant) => set((s) => ({
        user: user || s.user,
        tenant: tenant || s.tenant,
      })),
      
      logout: () => {
        // Call logout API if session-based
        if (get().authType === 'session') {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/auth/logout`, {
            method: 'POST',
            credentials: 'include'
          }).catch(() => {});
        }
        set({ token: null, user: null, tenant: null, isAuthenticated: false, authType: null });
      },
      
      getToken: () => get().token,
      getTenant: () => get().tenant,
      getAuthType: () => get().authType,
    }),
    {
      name: 'portal-auth-storage',
    }
  )
);

// Toast Store
export const useToastStore = create((set, get) => ({
  toasts: [],
  
  addToast: (toast) => {
    const id = Math.random().toString(36).slice(2, 11);
    const newToast = { id, ...toast };
    set({ toasts: [...get().toasts, newToast] });
    
    // Auto remove after duration
    setTimeout(() => {
      get().removeToast(id);
    }, toast.duration || 5000);
    
    return id;
  },
  
  removeToast: (id) => {
    set({ toasts: get().toasts.filter(t => t.id !== id) });
  },
  
  clearToasts: () => set({ toasts: [] }),
}));

// Convenience toast functions
export const toast = {
  success: (message, options = {}) => useToastStore.getState().addToast({ variant: 'success', message, ...options }),
  error: (message, options = {}) => useToastStore.getState().addToast({ variant: 'error', message, ...options }),
  warning: (message, options = {}) => useToastStore.getState().addToast({ variant: 'warning', message, ...options }),
  info: (message, options = {}) => useToastStore.getState().addToast({ variant: 'info', message, ...options }),
};

// Chat Store
export const useChatStore = create((set, get) => ({
  conversations: [],
  activeConversationId: null,
  messages: [],
  isStreaming: false,
  // Live docgen progress — kept here (not in PortalChat local state) so it
  // survives navigation between portal pages. Shape:
  //   { jobId, status, format, startedAt, error?, conversationId }
  docGenProgress: null,
  
  setConversations: (conversations) => set({ conversations }),
  
  setActiveConversation: (id) => set({ activeConversationId: id }),
  
  setMessages: (messages) => set({ messages }),
  
  addMessage: (message) => set({ messages: [...get().messages, message] }),
  
  updateLastMessage: (contentOrFn) => {
    const messages = get().messages;
    if (messages.length > 0) {
      const updated = [...messages];
      const last = updated[updated.length - 1];
      const newContent = typeof contentOrFn === 'function' 
        ? contentOrFn(last.content) 
        : contentOrFn;
      updated[updated.length - 1] = { ...last, content: newContent };
      set({ messages: updated });
    }
  },
  
  setStreaming: (isStreaming) => set({ isStreaming }),
  
  clearMessages: () => set({ messages: [], activeConversationId: null }),

  setDocGenProgress: (pOrFn) => {
    const prev = get().docGenProgress;
    const next = typeof pOrFn === 'function' ? pOrFn(prev) : pOrFn;
    set({ docGenProgress: next });
    try {
      if (next && next.jobId) {
        localStorage.setItem('cognara.docgen.active', JSON.stringify(next));
      } else {
        localStorage.removeItem('cognara.docgen.active');
      }
    } catch { /* localStorage may be unavailable */ }
  },
}));
